import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { glassPanel, glassTextField } from "./glass";
import { getAuthHeaders } from "./auth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const emptyForm = {
  name: "",
  description: "",
  priceDisplay: "",
  imageUrl: "",
  sortOrder: 0,
  active: true,
};

/**
 * Admin: catalog of services shown on the quote page (Local services + quote payload).
 */
function CreateJob() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [quoteSlug, setQuoteSlug] = useState("");
  const [quoteSlugDraft, setQuoteSlugDraft] = useState("");
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugMessage, setSlugMessage] = useState({ type: "", text: "" });

  const loadQuoteIdentity = useCallback(async () => {
    try {
      const { data } = await axios.get("/user/settings", { headers: getAuthHeaders() });
      const s = typeof data.quoteSlug === "string" ? data.quoteSlug.trim().toLowerCase() : "";
      setQuoteSlug(s);
      setQuoteSlugDraft(s);
    } catch {
      setQuoteSlug("");
      setQuoteSlugDraft("");
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/catalog-services/all", { headers: getAuthHeaders() });
      const list = data.services || [];
      setRows(list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } catch {
      setError("Could not load services. Sign in as an administrator and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadQuoteIdentity();
  }, [loadQuoteIdentity]);

  const quotePageUrl = quoteSlug ? `${window.location.origin}/quote/${quoteSlug}` : "";

  const copyQuoteLink = async () => {
    if (!quotePageUrl) return;
    try {
      await navigator.clipboard.writeText(quotePageUrl);
      setSlugMessage({ type: "success", text: "Link copied." });
    } catch {
      setSlugMessage({ type: "error", text: "Could not copy link." });
    }
  };

  const saveSlug = async () => {
    setSlugSaving(true);
    setSlugMessage({ type: "", text: "" });
    try {
      const next = quoteSlugDraft.trim().toLowerCase();
      await axios.patch("/user/quote-slug", { quoteSlug: next }, { headers: getAuthHeaders() });
      setQuoteSlug(next);
      setSlugMessage({ type: "success", text: "Your quote link was updated." });
    } catch (err) {
      setSlugMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update link.",
      });
    } finally {
      setSlugSaving(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sortOrder: rows.length });
    setDialogError("");
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      description: s.description || "",
      priceDisplay: s.priceDisplay,
      imageUrl: s.imageUrl || "",
      sortOrder: s.sortOrder ?? 0,
      active: s.active !== false,
    });
    setDialogError("");
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setDialogError("");
    try {
      if (editingId) {
        const { data } = await axios.patch(`/catalog-services/${editingId}`, form, {
          headers: getAuthHeaders(),
        });
        setRows((prev) =>
          prev
            .map((r) => (r.id === editingId ? data.service : r))
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        );
      } else {
        const { data } = await axios.post("/catalog-services", form, { headers: getAuthHeaders() });
        setRows((prev) =>
          [...prev, data.service].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        );
      }
      setDialogOpen(false);
    } catch (err) {
      setDialogError(err.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this service from the quote page and quote form?")) return;
    try {
      await axios.delete(`/catalog-services/${id}`, { headers: getAuthHeaders() });
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err.response?.data?.message || "Delete failed.");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          ...glassPanel,
          borderRadius: 3,
          p: 2.5,
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
          Your customer quote link
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          Share this URL with homeowners. It shows only the services you configure below (not the generic site catalog).
        </Typography>
        {quotePageUrl ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
            <TextField
              size="small"
              value={quotePageUrl}
              fullWidth
              sx={{ flex: "1 1 280px", ...glassTextField }}
              InputProps={{ readOnly: true }}
            />
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyQuoteLink}>
              Copy
            </Button>
            <Button
              component={RouterLink}
              to={quoteSlug ? `/quote/${quoteSlug}` : "/quote"}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              endIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
            >
              Open
            </Button>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Loading your link…
          </Typography>
        )}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Customize URL ending
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start", mb: 1 }}>
          <Typography variant="body2" sx={{ pt: 1, color: "text.secondary" }}>
            …/quote/
          </Typography>
          <TextField
            size="small"
            value={quoteSlugDraft}
            onChange={(e) => setQuoteSlugDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            placeholder="your-business"
            sx={{ width: 220, ...glassTextField }}
            helperText="3–40 chars: lowercase letters, numbers, hyphens."
          />
          <Button variant="contained" onClick={saveSlug} disabled={slugSaving || !quoteSlugDraft.trim()}>
            {slugSaving ? "Saving…" : "Save"}
          </Button>
        </Box>
        {slugMessage.text ? (
          <Alert
            severity={slugMessage.type === "success" ? "success" : "error"}
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setSlugMessage({ type: "", text: "" })}
          >
            {slugMessage.text}
          </Alert>
        ) : null}
      </Box>

      <Box
        sx={{
          ...glassPanel,
          borderRadius: 3,
          p: 2.5,
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
          My services (public catalog)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          These entries power <strong>Local services</strong> on <strong>your</strong> quote page and what customers can
          add to a <strong>Fast quote</strong>. Active services appear in order of <em>Sort order</em> (lower first).
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <Button variant="contained" onClick={openAdd}>
            Add service
          </Button>
          <Button variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outlined" onClick={() => loadQuoteIdentity()} size="small">
            Refresh link
          </Button>
        </Box>
        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading catalog…
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Typography component="div" variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No services on your quote page yet. Add at least one above — only your customers on{" "}
            {quotePageUrl ? (
              <Box component="strong" sx={{ wordBreak: "break-all" }}>
                {quotePageUrl}
              </Box>
            ) : (
              "your link"
            )}{" "}
            will see them. (The generic <strong>/quote</strong> page still shows the platform default catalog.)
          </Typography>
        ) : (
          rows.map((s) => (
            <Card
              key={s.id}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.65)",
                bgcolor: "rgba(255,255,255,0.45)",
              }}
            >
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {s.name}
                      {!s.active ? (
                        <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                          (hidden on site)
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      {s.priceDisplay}
                    </Typography>
                    {s.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.45 }}>
                        {s.description}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Sort {s.sortOrder ?? 0}
                      {s.resolvedImageUrl
                        ? ` · Image: ${s.hasStoredImage && !s.imageUrl ? "stored in database" : s.resolvedImageUrl}`
                        : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    <IconButton aria-label="Edit service" onClick={() => openEdit(s)} size="small" color="primary">
                      <EditOutlinedIcon />
                    </IconButton>
                    <IconButton aria-label="Delete service" onClick={() => remove(s.id)} size="small" color="error">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Edit service" : "Add service"}</DialogTitle>
        <DialogContent>
          {dialogError ? (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {dialogError}
            </Alert>
          ) : null}
          <TextField
            label="Name"
            required
            fullWidth
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mt: 1, mb: 2, ...glassTextField }}
          />
          <TextField
            label="Price (display)"
            required
            fullWidth
            placeholder="$45"
            value={form.priceDisplay}
            onChange={(e) => setForm((f) => ({ ...f, priceDisplay: e.target.value }))}
            sx={{ mb: 2, ...glassTextField }}
            helperText="Shown on cards and in quote emails exactly as typed."
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            sx={{ mb: 2, ...glassTextField }}
          />
          <TextField
            label="Image URL (optional)"
            fullWidth
            value={form.imageUrl}
            onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
            sx={{ mb: 2, ...glassTextField }}
            helperText="Optional external URL. Leave blank to use the image stored in the database (default test photos from backfill/seed), or a gradient if none."
          />
          <TextField
            label="Sort order"
            type="number"
            fullWidth
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))}
            sx={{ mb: 2, ...glassTextField }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
            }
            label="Active (show on quote page)"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.name.trim() || !form.priceDisplay.trim()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CreateJob;
